/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {Component} from 'react';
import PropTypes from "prop-types";
import Modal from 'react-modal';
import ReactPlayer from "react-player";
import {stripTags} from "../../helpers/filters";
import './ReactVideoModal.scss';

class ReactVideoModal extends Component {

    static propTypes = {
        isOpen: PropTypes.bool,
        onClose: PropTypes.func,
        uri: PropTypes.string.isRequired,
        inline: PropTypes.bool,
        inlineAlt: PropTypes.bool,
        eventMetaTitle: PropTypes.string,
        eventButtonTitle: PropTypes.string,
    };

    static defaultProps = {
        isOpen: false,
        onClose: () => {},
        inline: true,
        inlineAlt: false,
        eventMetaTitle: '',
        eventButtonTitle: ''
    };

    // eslint-disable-next-line react/sort-comp
    state = {
        secondsCurrentVideoHasPlayed: 0,
        longestPercentageViewed: 0,
        viewedThirtySecondsAndMoreThanHalfOfVideo: false,
        eventHasRunStarted: false,
        eventHasRunQuarter: false,
        eventHasRunHalf: false,
        eventHasRunThreeQuarters: false,
        eventHasRunComplete: false
    };

    /**
     * Get Video ID from URL
     *
     * @param url
     * @return {*}
     */
    static getVideoId = url => {
        if (!url) return null;

        // YouTube
        if (url.includes('youtube.com/watch?v=')) {
            return {type: 'youtube', id: url.split('?v=')[1]};
        }
        if (url.includes('youtube.com/embed/')) {
            return {type: 'youtube', id: url.split('embed/')[1]};
        }
        else if (url.includes('youtu.be/')) {
            return {type: 'youtube', id: url.split('youtu.be/')[1]};
        }

        // Vimeo - Extract numeric ID from various URL formats
        // Matches: vimeo.com/123456, vimeo.com/123456/, vimeo.com/123456?params, vimeo.com/channels/staff/123456, etc.
        const vimeoRegex = /https?:\/\/(www\.)?vimeo\.com\/(?:.*\/)?(\d+)(?:[?/].*)?$/;
        const vimeoUrl = url.match(vimeoRegex);
        if (vimeoUrl && vimeoUrl[2]) {
            return {type: 'vimeo', id: vimeoUrl[2]};
        }

        return null;
    };

    getEventMetaTitle = () => {
        const metaTitle = stripTags(this.props.eventMetaTitle);
        if (!metaTitle) return stripTags(document.title);

        return metaTitle;
    }

    getEventButtonTitle = () => {
        const buttonTitle = stripTags(this.props.eventButtonTitle);
        if (!buttonTitle) return '';

        return buttonTitle;
    }

    /**
     * Track GTM Event on Video Start
     *
     * @param currentVideo
     */
    handleVideoStart = (currentVideo) => {
        if (this.state.eventHasRunStarted) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': `maVimeoProgress`,
            'progressVideoTitle': `${currentVideo.type} ${currentVideo.id} on ${this.getEventMetaTitle()}`,
            'meta_title': this.getEventMetaTitle(),
            'button_title': this.getEventButtonTitle(),
            'progressVimeoType': currentVideo.type,
            'progressVimeoID': currentVideo.id,
            'progressAmount': 'started'
        });

        this.setState({eventHasRunStarted: true});
    };

    /**
     * Track GTM Event on Video Completion
     *
     * @param currentVideo
     */
    handleVideoEnded = (currentVideo) => {
        if (this.state.eventHasRunComplete) return;

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': `maVimeoProgress`,
            'progressVideoTitle': `${currentVideo.type} ${currentVideo.id} on ${this.getEventMetaTitle()}`,
            'meta_title': this.getEventMetaTitle(),
            'button_title': this.getEventButtonTitle(),
            'progressVimeoType': currentVideo.type,
            'progressVimeoID': currentVideo.id,
            'progressAmount': 'completed'
        });

        this.setState({eventHasRunComplete: true});
    };

    /**
     * Track GTM Event by Video Progress
     *
     * @param currentVideo
     * @param progress
     */
    handleVideoProgress = (currentVideo, progress) => {
        const percentagePlayed = Math.round(progress.played * 100);

        window.dataLayer = window.dataLayer || [];

        // 25%
        if (this.state.longestPercentageViewed >= 25 && !this.state.eventHasRunQuarter) {
            window.dataLayer.push({
                'event': `maVimeoProgress`,
                'progressVideoTitle': `${currentVideo.type} ${currentVideo.id} on ${this.getEventMetaTitle()}`,
                'meta_title': this.getEventMetaTitle(),
                'button_title': this.getEventButtonTitle(),
                'progressVimeoType': currentVideo.type,
                'progressVimeoID': currentVideo.id,
                'progressAmount': '25%'
            });
            this.setState({eventHasRunQuarter: true});
        }

        // 50%
        if (this.state.longestPercentageViewed >= 50 && !this.state.eventHasRunHalf) {
            window.dataLayer.push({
                'event': `maVimeoProgress`,
                'progressVideoTitle': `${currentVideo.type} ${currentVideo.id} on ${this.getEventMetaTitle()}`,
                'meta_title': this.getEventMetaTitle(),
                'button_title': this.getEventButtonTitle(),
                'progressVimeoType': currentVideo.type,
                'progressVimeoID': currentVideo.id,
                'progressAmount': '50%'
            });
            this.setState({eventHasRunHalf: true});
        }

        // 75%%
        if (this.state.longestPercentageViewed >= 75 && !this.state.eventHasRunThreeQuarters) {
            window.dataLayer.push({
                'event': `maVimeoProgress`,
                'progressVideoTitle': `${currentVideo.type} ${currentVideo.id} on ${this.getEventMetaTitle()}`,
                'meta_title': this.getEventMetaTitle(),
                'button_title': this.getEventButtonTitle(),
                'progressVimeoType': currentVideo.type,
                'progressVimeoID': currentVideo.id,
                'progressAmount': '75%'
            });
            this.setState({eventHasRunThreeQuarters: true});
        }

        // Greater than 50% and Viewed at least 30 seconds
        if (this.state.longestPercentageViewed >= 50 && this.state.secondsCurrentVideoHasPlayed >= 30 && !this.state.viewedThirtySecondsAndMoreThanHalfOfVideo) {
            window.dataLayer.push({
                'event': `maVimeoProgress`,
                'progressVideoTitle': `${currentVideo.type} ${currentVideo.id} on ${this.getEventMetaTitle()}`,
                'meta_title': this.getEventMetaTitle(),
                'button_title': this.getEventButtonTitle(),
                'progressVimeoType': currentVideo.type,
                'progressVimeoID': currentVideo.id,
                'progressAmount': '30s'
            });
            this.setState({viewedThirtySecondsAndMoreThanHalfOfVideo: true})
        }

        // Increment seconds played counter and longest percentage reached
        this.setState({
            secondsCurrentVideoHasPlayed: this.state.secondsCurrentVideoHasPlayed += 1,
            longestPercentageViewed: percentagePlayed > this.state.longestPercentageViewed ? percentagePlayed : this.state.longestPercentageViewed
        });
    };

    /**
     * Execute parent close when Modal closes
     */
    handleModalClose = () => {
        this.resetVideoState();

        this.props.onClose();
    };

    /**
     * Reset Video State
     */
    resetVideoState = () => {
        this.setState({
            secondsCurrentVideoHasPlayed: 0,
            longestPercentageViewed: 0,
            viewedThirtySecondsAndMoreThanHalfOfVideo: false,
            eventHasRunStarted: false,
            eventHasRunQuarter: false,
            eventHasRunHalf: false,
            eventHasRunThreeQuarters: false,
            eventHasRunComplete: false
        });
    };

    renderPlayer(currentVideo, url, playing, classes = "react-modal reactVideoModal__player") {
        return (
            <ReactPlayer className={classes}
                         controls
                         progressInterval={1000}
                         width='100%'
                         height='100%'
                         loop={false}
                         onStart={() => this.handleVideoStart(currentVideo)}
                         onProgress={progress => this.handleVideoProgress(currentVideo, progress)}
                         onEnded={() => this.handleVideoEnded(currentVideo)}
                         url={url}
                         playing={playing}
            />
        );
    }

    render() {
        const {uri, inline, inlineAlt} = this.props;

        const currentVideo = ReactVideoModal.getVideoId(uri);
        if (!currentVideo) return null;

        const url = currentVideo.type === 'vimeo' ? `https://player.vimeo.com/video/${currentVideo.id}?title=0&byline=0&portrait=0&autoplay=1` : uri;

        if (inlineAlt) return (
            <div>
                {this.renderPlayer(currentVideo, url, this.props.isOpen, 'reactVideoInline__player')}
            </div>
        );

        if (inline) return (
            <div className={`reactVideoInline${this.props.isOpen ? ' reactVideoInline--active' : ''}`}>
                {this.renderPlayer(currentVideo, url, this.props.isOpen)}
            </div>
        );

        return (
            <Modal className="reactVideoModal" overlayClassName="reactVideoModal__overlay" isOpen={this.props.isOpen} onRequestClose={this.handleModalClose}>

                <button onClick={this.handleModalClose} className="reactVideoModal__close" aria-label="Close the Video Modal"/>

                <div className="reactVideoModal__outer" onClick={this.handleModalClose}>
                    <div className="reactVideoModal__playerWrapper">
                        {this.renderPlayer(currentVideo, url)}
                    </div>
                </div>
            </Modal>
        );
    }

}

export default ReactVideoModal;



// WEBPACK FOOTER //
// ./frontend/components/ReactVideoModal/ReactVideoModal.jsx